import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getShowDetails, getStreamingSources } from '../../services/watchmodeApi';
import { 
  getShowAverageRating, 
  addRating,
  getShowFromUserLists,
  addToWatchlist,
  getShowRatings,
  markShowAsSeen,
  markShowAsWatching,
  archiveShow
} from '../../services/databaseService';
import { useAuth } from '../../context/AuthContext';
import './ShowDetails.css';
import { StrawberryIcon } from '../../components/icons/StrawberryIcon';
import { RatingStrawberries } from '../../components/RatingStrawberries';
import { RatingOnlyModal } from '../../components/RatingOnlyModal';
import { doc, setDoc, serverTimestamp, getDoc, collection, onSnapshot, deleteDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Spinner } from '../../components/Spinner/Spinner';
import { getIMDbRating, getIMDbDetails } from '../../services/omdbApi';
import RatingsOverviewModal from '../../components/RatingsOverviewModal/RatingsOverviewModal';
import { Toast } from '../../components/Toast/Toast';
import { getUserRating } from '../../services/databaseService';
import { StreamingSourceModal } from '../../components/StreamingSourceModal/StreamingSourceModal';
import LoginPrompt from '../../components/LoginPrompt/LoginPrompt';
import { pageStateStore } from '../../services/pageStateStore';
import { createPortal } from 'react-dom';
import { addActivity } from '../../services/databaseService';

const SHOW_STATUSES = {
  NONE: 'none',
  WATCHING: 'watching',
  WATCHLIST: 'watchlist',
  SEEN: 'seen',
  ARCHIVED: 'archived'
};

const getStatusLabel = (status) => {
  switch (status) {
    case SHOW_STATUSES.WATCHING:
      return 'Tittar på';
    case SHOW_STATUSES.WATCHLIST:
      return 'Ska se';
    case SHOW_STATUSES.SEEN:
      return 'Har sett';
    case SHOW_STATUSES.ARCHIVED:
      return 'Arkiverad';
    default:
      return 'Status';
  }
};

const fetchAndStoreShowData = async (id, imdbId) => {
  try {
    console.log('Fetching data for show:', { id, imdbId });

    // First get existing show data if it exists
    const existingDoc = await getDoc(doc(db, 'shows', id.toString()));
    const existingData = existingDoc.exists() ? existingDoc.data() : {};

    // First get watchmode data to ensure we have an IMDB ID
    const watchmodeData = await getShowDetails(id);
    const finalImdbId = imdbId || watchmodeData.imdb_id;

    // Then fetch all data in parallel
    const [imdbData, streamingSources] = await Promise.all([
      finalImdbId ? getIMDbDetails(finalImdbId) : null,
      getStreamingSources(id)
    ]);

    console.log('Type information:', {
      existingType: existingData.type,
      imdbType: imdbData?.Type,
      watchmodeType: watchmodeData?.type
    });

    // Determine type with explicit logic
    const showType = imdbData?.Type?.toLowerCase() === 'movie' || 
                    imdbData?.Type?.toLowerCase() === 'film' || 
                    watchmodeData?.type?.toLowerCase() === 'movie' || 
                    watchmodeData?.type?.toLowerCase() === 'film' 
                    ? 'movie' : 'tv_series';

    console.log('Determined show type:', showType);

    // Combine data, preserving existing rating data
    const showData = {
      id: id,
      title: imdbData?.Title || watchmodeData.title || '',
      year: imdbData?.Year || watchmodeData.year || null,
      poster: (imdbData?.Poster && imdbData.Poster !== 'N/A') ? imdbData.Poster : 
             (watchmodeData.poster || null),
      backdrop: watchmodeData.backdrop || null,
      plot_overview: imdbData?.Plot || watchmodeData.plot_overview || '',
      imdb_id: finalImdbId || null,
      imdb_rating: imdbData?.imdbRating || null,
      total_seasons: imdbData?.totalSeasons ? parseInt(imdbData.totalSeasons) : null,
      actors: imdbData?.Actors || watchmodeData.cast_summary || '',
      creators: imdbData?.Writer || watchmodeData.creators_summary || '',
      genre: imdbData?.Genre || '',
      runtime: imdbData?.Runtime || '',
      rated: imdbData?.Rated || '',
      streamingSources: Array.isArray(streamingSources) ? streamingSources : [],
      type: showType,
      ratingCount: existingData.ratingCount || 0,
      ratingSum: existingData.ratingSum || 0,
      averageRating: existingData.averageRating || 0,
      createdAt: existingData.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    console.log('Final show data:', showData);

    // Use merge option to preserve any fields we haven't explicitly set
    await setDoc(doc(db, 'shows', id.toString()), showData, { merge: true });

    return showData;
  } catch (error) {
    console.error('Error fetching and storing show data:', error);
    throw error;
  }
};

function ShowDetails() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  
  // Add cache check at the start
  const getCachedShow = useCallback(() => {
    const cached = sessionStorage.getItem(`show_${id}`);
    if (cached) {
      const { show, timestamp } = JSON.parse(cached);
      const now = Date.now();
      if (now - timestamp < 5 * 60 * 1000) { // 5 minute cache
        return show;
      }
    }
    return null;
  }, [id]);

  const cachedShow = useMemo(() => getCachedShow(), [getCachedShow]);
  
  // Initialize with cached data if available
  const [show, setShow] = useState(cachedShow);
  const [loading, setLoading] = useState(!cachedShow);
  const [error, setError] = useState(null);
  const [sources, setSources] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [showRatingOnlyModal, setShowRatingOnlyModal] = useState(false);
  const [selectedShowForRating, setSelectedShowForRating] = useState(null);
  const [showRatingsOverview, setShowRatingsOverview] = useState(false);
  const [showRatings, setShowRatings] = useState([]);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showStreamingModal, setShowStreamingModal] = useState(false);
  const [selectedShowForSources, setSelectedShowForSources] = useState(null);
  const [showStatus, setShowStatus] = useState(SHOW_STATUSES.NONE);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });

  // Track mounted state with ref instead of variable
  const isMounted = useRef(true);
  const initialFetchDone = useRef(false);
  const effectCleanupCount = useRef(0);
  const fetchInProgress = useRef(false); // Add this to prevent concurrent fetches
  
  // Wrap state updates in stable functions
  const updateShow = useCallback((newData) => {
    if (!isMounted.current) return;
    
    setShow(prev => {
      if (!prev && !newData) return prev;
      if (prev?.id === newData?.id && 
          prev?.averageRating === newData?.averageRating && 
          prev?.ratingCount === newData?.ratingCount) {
        return prev;
      }
      return newData;
    });
  }, []);

  const updateStatus = useCallback((newStatus) => {
    if (!isMounted.current) return;
    setShowStatus(prev => prev === newStatus ? prev : newStatus);
  }, []);

  const updateLoading = useCallback((isLoading) => {
    if (!isMounted.current) return;
    setLoading(prev => prev === isLoading ? prev : isLoading);
  }, []);

  // Add these refs to store the unsubscribe functions
  const unsubscribeStatusRef = useRef(() => {});
  const unsubscribeShowRef = useRef(() => {});

  // Single effect to handle all show data fetching
  useEffect(() => {
    console.log('ShowDetails: Main effect running');
    if (!id) return;
    
    isMounted.current = true; // Ensure mounted flag is set
    let unsubscribeShow = () => {};
    let unsubscribeStatus = () => {};
    
    const setupListeners = async () => {
      try {
        if (!cachedShow) {
          updateLoading(true);
        }
        setError(null);
        
        // First check if we have the show in Firestore
        const showDoc = await getDoc(doc(db, 'shows', id.toString()));
        
        if (showDoc.exists()) {
          console.log('Found show in Firestore:', showDoc.data());
          const showData = showDoc.data();
          updateShow(showData);
          
          // Cache the show data
          sessionStorage.setItem(`show_${id}`, JSON.stringify({
            show: showData,
            timestamp: Date.now()
          }));
        } else {
          console.log('Fetching show from APIs...');
          // When fetching new show data, explicitly preserve the type
          const showData = await fetchAndStoreShowData(id);
          console.log('Fetched new show data:', showData);
          
          // Ensure type is correctly set before updating state
          const finalShowData = {
            ...showData,
            type: showData.type || (showData.total_seasons ? 'tv_series' : 'movie')
          };
          
          updateShow(finalShowData);
          
          // Cache the new show data
          sessionStorage.setItem(`show_${id}`, JSON.stringify({
            show: finalShowData,
            timestamp: Date.now()
          }));
        }

        // Only set up real-time listeners if user is authenticated
        if (user?.uid) {
          // Set up real-time listener for show updates
          unsubscribeShow = onSnapshot(
            doc(db, 'shows', id.toString()),
            (doc) => {
              if (doc.exists()) {
                console.log('Show update received:', doc.data());
                updateShow(doc.data());
              }
            },
            (error) => {
              console.error('Error in show listener:', error);
            }
          );

          // Set up status listeners
          const watchlistRef = doc(db, 'users', user.uid, 'watchlist', id.toString());
          const seenRef = doc(db, 'users', user.uid, 'seen', id.toString());
          const watchingRef = doc(db, 'users', user.uid, 'watching', id.toString());
          const archivedRef = doc(db, 'users', user.uid, 'archived', id.toString());

          // Check initial status
          const [watchlistDoc, seenDoc, watchingDoc, archivedDoc] = await Promise.all([
            getDoc(watchlistRef),
            getDoc(seenRef),
            getDoc(watchingRef),
            getDoc(archivedRef)
          ]);

          // Set initial status
          if (watchingDoc.exists()) {
            updateStatus(SHOW_STATUSES.WATCHING);
          } else if (watchlistDoc.exists()) {
            updateStatus(SHOW_STATUSES.WATCHLIST);
          } else if (seenDoc.exists()) {
            updateStatus(SHOW_STATUSES.SEEN);
          } else if (archivedDoc.exists()) {
            updateStatus(SHOW_STATUSES.ARCHIVED);
          } else {
            updateStatus(SHOW_STATUSES.NONE);
          }

          // Set up real-time listeners for all collections
          const unsubscribeWatchlist = onSnapshot(watchlistRef, (doc) => {
            if (doc.exists()) {
              updateStatus(SHOW_STATUSES.WATCHLIST);
            }
          });

          const unsubscribeSeen = onSnapshot(seenRef, (doc) => {
            if (doc.exists()) {
              updateStatus(SHOW_STATUSES.SEEN);
            }
          });

          const unsubscribeWatching = onSnapshot(watchingRef, (doc) => {
            if (doc.exists()) {
              updateStatus(SHOW_STATUSES.WATCHING);
            }
          });

          const unsubscribeArchived = onSnapshot(archivedRef, (doc) => {
            if (doc.exists()) {
              updateStatus(SHOW_STATUSES.ARCHIVED);
            }
          });

          unsubscribeStatus = () => {
            unsubscribeWatchlist();
            unsubscribeSeen();
            unsubscribeWatching();
            unsubscribeArchived();
          };
        }

      } catch (error) {
        console.error('Error setting up show data:', error);
        setError('Could not load show details');
      } finally {
        updateLoading(false);
      }
    };

    setupListeners();

    return () => {
      console.log('ShowDetails: Main effect cleanup running');
      // Ensure cleanup runs synchronously
      try {
        unsubscribeShow();
        unsubscribeStatus();
        isMounted.current = false;
        updateLoading(false);
      } catch (error) {
        console.error('Error during cleanup:', error);
      }
    };
  }, [id, user?.uid, updateShow, updateStatus, updateLoading]);

  useEffect(() => {
    const originalScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    
    return () => {
      // Only restore if the component is actually unmounting
      if (!isMounted.current) {
        window.history.scrollRestoration = originalScrollRestoration;
      }
    };
  }, []);

  // Memoize render data
  const renderData = useMemo(() => ({
    showId: show?.id,
    status: showStatus,
    loading: (!show && loading) || authLoading, // Only show loading if we don't have show data
    error
  }), [show, showStatus, loading, authLoading, error]);

  // Only log when render data actually changes
  useEffect(() => {
    console.log('Rendering with:', renderData);
  }, [renderData]);

  const handleProtectedAction = () => {
    if (!user) {
      // Store the current URL for return after login
      localStorage.setItem('returnTo', window.location.href);
      // Redirect to login
      navigate('/login');
      return false;
    }
    return true;
  };

  const handleRatingSubmit = async (rating) => {
    if (!handleProtectedAction()) return;
    try {
      if (user && show) {
        const { averageRating, ratingCount } = await addRating(user.uid, show.id, rating);
        
        // Add activity tracking
        await addActivity(
          user.uid,
          'rating',
          show.id,
          show.title,
          rating
        );
        
        // Update local state
        setShow(prev => ({
          ...prev,
          averageRating,
          ratingCount
        }));
        setToast({ message: 'Betyg sparat!', type: 'success' });
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      setToast({ message: 'Kunde inte spara betyg', type: 'error' });
    }
  };

  const handleAddToWatchlist = async () => {
    if (!handleProtectedAction()) return;
    if (!user || !show) return;

    try {
      await addToWatchlist(user.uid, {
        ...show,
        addedAt: new Date()
      });
      
      // Add activity tracking
      await addActivity(
        user.uid,
        'status',
        show.id,
        show.title,
        'watchlist'
      );
      
      setToast({ message: 'Sparad i din lista!', type: 'success' });
    } catch (error) {
      console.error('Error adding to watchlist:', error);
      setToast({ message: 'Kunde inte spara', type: 'error' });
    }
  };

  const fetchRatings = async () => {
    if (!show?.id) return;
    setRatingsLoading(true);
    try {
      const ratingsData = await getShowRatings(show.id);
      
      // Only fetch user details if user is authenticated
      if (user) {
        const ratingsWithUserDetails = await Promise.all(
          ratingsData.ratings.map(async (rating) => {
            const userDoc = await getDoc(doc(db, 'users', rating.userId));
            const userData = userDoc.data();
            
            // Get user name with fallback logic for Apple Sign In
            let userName = 'Anonymous';
            if (userData?.displayName) {
              userName = userData.displayName;
            } else if (userData?.firstName || userData?.lastName) {
              userName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
            }
        
            return {
              ...rating,
              userName,
              userPhoto: userData?.photoURL
            };
          })
        );
        setShowRatings(ratingsWithUserDetails);
      } else {
        // For unauthenticated users, just show anonymous ratings
        const anonymousRatings = ratingsData.ratings.map(rating => ({
          ...rating,
          userName: 'Anonymous',
          userPhoto: null
        }));
        setShowRatings(anonymousRatings);
      }
    } catch (error) {
      console.error('Error fetching ratings:', error);
    } finally {
      setRatingsLoading(false);
    }
  };

  useEffect(() => {
    fetchRatings();
  }, [show]);

  // Add this effect to listen for rating changes
  useEffect(() => {
    if (!user || !show?.id) return;

    const ratingsRef = collection(db, `shows/${show.id}/ratings`);
    const unsubscribe = onSnapshot(ratingsRef, async () => {
      if (!isMounted.current) return;
      // Get fresh ratings whenever there's a change
      const ratingsData = await getShowRatings(show.id);
      setShowRatings(ratingsData.ratings);
      
      // Update show's aggregate data
      setShow(prev => ({
        ...prev,
        averageRating: ratingsData.averageRating,
        ratingCount: ratingsData.ratingCount
      }));
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [show?.id, user]);

  const handleAddStreamingSource = async (source) => {
    if (!handleProtectedAction()) return;

    try {
      // Show success toast immediately
      setToast({ message: 'Streamingtjänst tillagd!', type: 'success' });

      // Update local state immediately
      setShow(prev => ({
        ...prev,
        streamingSources: [...(prev.streamingSources || []), source]
      }));

      // Update in database
      await updateDoc(doc(db, 'shows', show.id.toString()), {
        streamingSources: arrayUnion(source),
        updatedAt: serverTimestamp()
      });

      // Add activity tracking
      await addActivity(
        user.uid,
        'streaming',
        show.id,
        show.title,
        source
      );
    } catch (error) {
      console.error('Error adding streaming source:', error);
      setToast({ message: 'Kunde inte lägga till streamingtjänst', type: 'error' });
      // Revert local state
      setShow(prev => ({
        ...prev,
        streamingSources: prev.streamingSources?.filter(s => s !== source) || []
      }));
    }
  };

  const handleRemoveStreamingSource = async (source) => {
    if (!handleProtectedAction()) return;

    try {
      // Show success toast immediately
      setToast({ message: 'Streamingtjänst borttagen', type: 'success' });

      // Update local state immediately
      setShow(prev => ({
        ...prev,
        streamingSources: prev.streamingSources?.filter(s => s !== source) || []
      }));

      // Update in database
      await updateDoc(doc(db, 'shows', show.id.toString()), {
        streamingSources: arrayRemove(source),
        updatedAt: serverTimestamp()
      });

      // Add activity tracking for removal
      await addActivity(
        user.uid,
        'streaming_remove',
        show.id,
        show.title,
        source
      );
    } catch (error) {
      console.error('Error removing streaming source:', error);
      setToast({ message: 'Kunde inte ta bort streamingtjänst', type: 'error' });
      // Revert local state
      setShow(prev => ({
        ...prev,
        streamingSources: [...(prev.streamingSources || []), source]
      }));
    }
  };

  const handleMarkAsSeen = async () => {
    if (!handleProtectedAction()) return;
    if (!user || !show) return;

    try {
      await setDoc(doc(db, 'users', user.uid, 'seen', show.id.toString()), {
        ...show,
        addedAt: serverTimestamp(),
        status: SHOW_STATUSES.SEEN
      });
      
      // Add activity tracking
      await addActivity(
        user.uid,
        'status',
        show.id,
        show.title,
        'seen'
      );
      
      // Remove from watchlist if it exists there
      const watchlistRef = doc(db, 'users', user.uid, 'watchlist', show.id.toString());
      const watchlistDoc = await getDoc(watchlistRef);
      if (watchlistDoc.exists()) {
        await deleteDoc(watchlistRef);
      }

      setToast({ message: 'Markerad som sedd!', type: 'success' });
    } catch (error) {
      console.error('Error marking as seen:', error);
      setToast({ message: 'Kunde inte markera som sedd', type: 'error' });
    }
  };

  const handleArchiveShow = async () => {
    if (!user || !show) return;

    try {
      setToastState({ show: true, message: '', showSpinner: true });
      // Add your archive function here
      // await archiveShow(user.uid, show);
      setShowStatus('archived');
      setToastState({ show: true, message: 'Arkiverad!', showSpinner: false });
    } catch (error) {
      console.error('Error archiving show:', error);
      setToastState({ show: true, message: 'Kunde inte arkivera', showSpinner: false });
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleShare = async () => {
    if (!handleProtectedAction()) return;
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: show.title,
          text: `Kolla in ${show.title} på Smultron!`,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setToast({ message: 'Länk kopierad!', type: 'success' });
      }

      // Add activity tracking for share with a value
      await addActivity(
        user.uid,
        'share',
        show.id,
        show.title,
        'shared'  // Add this value parameter
      );
    } catch (error) {
      console.error('Error sharing:', error);
      if (error.name !== 'AbortError') {
        setToast({ message: 'Kunde inte dela', type: 'error' });
      }
    }
  };

  const handleRatingClick = () => {
    if (!handleProtectedAction()) return;
    
    setSelectedShowForRating(show);
    setShowRatingOnlyModal(true);
  };

  const handleMarkAsWatching = async () => {
    if (!handleProtectedAction()) return;
    if (!user || !show) return;

    try {
      await markShowAsWatching(user.uid, show);
      // Add activity tracking
      await addActivity(
        user.uid,
        'status',
        show.id,
        show.title,
        'watching'
      );
      setToast({ message: 'Markerad som pågående!', type: 'success' });
    } catch (error) {
      console.error('Error marking as watching:', error);
      setToast({ message: 'Kunde inte markera som pågående', type: 'error' });
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!handleProtectedAction()) return;
    if (!user || !show) return;

    try {
      switch (newStatus) {
        case SHOW_STATUSES.WATCHLIST:
          await addToWatchlist(user.uid, show);
          await addActivity(user.uid, 'status', show.id, show.title, 'watchlist');
          setToast({ message: 'Tillagd i Ska se!', type: 'success' });
          break;
        case SHOW_STATUSES.WATCHING:
          await markShowAsWatching(user.uid, show);
          await addActivity(user.uid, 'status', show.id, show.title, 'watching');
          setToast({ message: 'Tillagd i Tittar på!', type: 'success' });
          break;
        case SHOW_STATUSES.SEEN:
          await markShowAsSeen(user.uid, show);
          await addActivity(user.uid, 'status', show.id, show.title, 'seen');
          setToast({ message: 'Markerad som sedd!', type: 'success' });
          break;
        case SHOW_STATUSES.ARCHIVED:
          await archiveShow(user.uid, show);
          await addActivity(user.uid, 'status', show.id, show.title, 'archived');
          setToast({ message: 'Arkiverad!', type: 'success' });
          break;
        default:
          break;
      }
      setShowStatusMenu(false);
    } catch (error) {
      console.error('Error changing status:', error);
      setToast({ message: 'Kunde inte ändra status', type: 'error' });
    }
  };

  const handleStatusClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!handleProtectedAction()) return;
    
    const buttonRect = event.currentTarget.getBoundingClientRect();
    
    // Position menu to the left of the button by default
    let left = buttonRect.left - 220; // menu width (200px) + gap (20px)
    let top = buttonRect.top;
    
    // If not enough space on the left, position to the right
    if (left < 16) {
      left = buttonRect.right + 20;
    }
    
    // If not enough space on either side, position below
    if (left + 200 > window.innerWidth - 16) {
      left = buttonRect.left;
      top = buttonRect.bottom + 8;
    }
    
    setMenuPosition({ left, top });
    setShowStatusMenu(true);
  };

  const StatusMenu = ({ show, position, onClose, currentStatus, onStatusChange }) => {
    return createPortal(
      <>
        <div className="popular-status-menu-overlay" onClick={onClose} />
        <div 
          className="popular-status-menu" 
          style={{ 
            position: 'fixed',
            left: `${position.left}px`,
            top: `${position.top}px`
          }}
        >
          <button 
            className={`status-option ${currentStatus === SHOW_STATUSES.WATCHING ? 'active' : ''}`}
            onClick={() => onStatusChange(SHOW_STATUSES.WATCHING)}
          >
            <i className="fas fa-satellite-dish"></i>
            <span>Tittar på</span>
          </button>
          <button 
            className={`status-option ${currentStatus === SHOW_STATUSES.WATCHLIST ? 'active' : ''}`}
            onClick={() => onStatusChange(SHOW_STATUSES.WATCHLIST)}
          >
            <i className="fas fa-check"></i>
            <span>Ska se</span>
          </button>
          <button 
            className={`status-option ${currentStatus === SHOW_STATUSES.SEEN ? 'active' : ''}`}
            onClick={() => onStatusChange(SHOW_STATUSES.SEEN)}
          >
            <i className="fas fa-check-double"></i>
            <span>Har sett</span>
          </button>
          <button 
            className={`status-option ${currentStatus === SHOW_STATUSES.ARCHIVED ? 'active' : ''}`}
            onClick={() => onStatusChange(SHOW_STATUSES.ARCHIVED)}
          >
            <i className="fas fa-trash"></i>
            <span>Arkivera</span>
          </button>
        </div>
      </>,
      document.body
    );
  };

  useEffect(() => {
    return () => {
      console.log('ShowDetails: Cleanup running');
      isMounted.current = false;
    };
  }, []);

  // Add this effect to handle browser back button
  useEffect(() => {
    const handlePopState = (event) => {
      // The state is automatically restored by the browser
      // We just need to ensure smooth scrolling
      const savedState = pageStateStore.getState('friendProfile');
      if (savedState) {
        setTimeout(() => {
          window.scrollTo(0, savedState.scrollPosition || 0);
        }, 100);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className="show-details-container">
        <div className="skeleton-backdrop skeleton"></div>
        <div className="skeleton-content">
          <div className="skeleton skeleton-title"></div>
          <div className="skeleton skeleton-metadata"></div>
          <div className="skeleton-buttons">
            <div className="skeleton skeleton-button"></div>
            <div className="skeleton skeleton-button"></div>
            <div className="skeleton skeleton-button"></div>
          </div>
          <div className="skeleton skeleton-plot"></div>
        </div>
      </div>
    );
  }

  // Show error message
  if (error) {
    return <div className="show-details-container">{error}</div>;
  }

  // Show
  if (!show) {
    return <div className="show-details-container">Serien kunde inte hittas</div>;
  }

  return (
    <div className="show-details-container">
      <div className="backdrop-image" style={{ backgroundImage: `url(${show.backdrop})` }}>
        <div className="backdrop-overlay"></div>
      </div>
      
      <div className="show-details-content">
        <div className="show-details-info">
          <h1 className="show-title">{show.title}</h1>
          
          <div className="metadata">
            <span className="year">{show.year}</span>
            {show.type && (
              <>
                <span className="dot-divider"> • </span>
                <span className="type">{show.type === 'movie' ? 'Film' : 'Serie'}</span>
              </>
            )}
            {show.total_seasons && (
              <>
                <span className="dot-divider"> • </span>
                <span className="seasons">
                  {show.total_seasons} {show.total_seasons === 1 ? 'säsong' : 'säsonger'}
                </span>
              </>
            )}
            {show.imdb_rating && (
              <div className="imdb-container">
                <span className="imdb-badge">IMDB</span>
                <span className="imdb-score">{show.imdb_rating}</span>
              </div>
            )}
          </div>

          {show?.ratingCount > 0 && (
            <div className="rating-section">
              <RatingStrawberries 
                rating={show.averageRating}
                ratingCount={show.ratingCount}
                onClick={() => {
                  if (!handleProtectedAction()) return;
                  setShowRatingsOverview(true);
                  fetchRatings();
                }}
                style={{ cursor: 'pointer', outline: 'none' }}
              />
            </div>
          )}

          <div className="streaming-services">
            {show.streamingSources?.map((source, i) => (
              <span 
                key={i} 
                className="streaming-service"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedShowForSources(show);
                  setShowStreamingModal(true);
                }}
                style={{ cursor: 'pointer' }}
              >
                {source}
              </span>
            ))}
            {!show.streamingSources?.length && (
              <button 
                className="add-streaming-button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedShowForSources(show);
                  setShowStreamingModal(true);
                }}
              >
                <i className="fas fa-plus"></i>
                <span>Lägg till plattform</span>
              </button>
            )}
          </div>

          <div className="action-buttons">
            {/* Status button with dropdown */}
            <div className="status-button-container">
              <button 
                className={`action-button status-button ${showStatus !== SHOW_STATUSES.NONE ? 'active' : ''}`}
                onClick={handleStatusClick}
                title={getStatusLabel(showStatus)}
              >
                <i className={`fas ${
                  showStatus === SHOW_STATUSES.SEEN ? 'fa-check-double' :
                  showStatus === SHOW_STATUSES.WATCHING ? 'fa-satellite-dish' :
                  showStatus === SHOW_STATUSES.WATCHLIST ? 'fa-check' :
                  showStatus === SHOW_STATUSES.ARCHIVED ? 'fa-trash' :
                  'fa-plus'
                }`}></i>
                <span className="action-label">{getStatusLabel(showStatus)}</span>
              </button>

              {showStatusMenu && (
                <StatusMenu
                  show={show}
                  position={menuPosition}
                  onClose={() => setShowStatusMenu(false)}
                  currentStatus={showStatus}
                  onStatusChange={handleStatusChange}
                />
              )}
            </div>

            {/* Keep existing Rating and Share buttons */}
            <button 
              className="action-button rating-button"
              onClick={handleRatingClick}
              title="Rösta"
            >
              <i className="fas fa-thumbs-up"></i>
              <span className="action-label">Rösta</span>
            </button>

            <button 
              className="action-button share-button"
              onClick={handleShare}
              title="Dela"
            >
              <i className="fas fa-share-alt"></i>
              <span className="action-label">Dela</span>
            </button>
          </div>

          <p className="plot">{show.plot_overview}</p>

          <div className="show-metadata" style={{ marginTop: '2rem' }}>
            <div className="metadata-grid">
              {show.creators && (
                <div className="creators-section metadata-item">
                  <h3>Skapare</h3>
                  <p>{show.creators}</p>
                </div>
              )}
              
              {show.actors && (
                <div className="actors-section metadata-item">
                  <h3>Skådespelare</h3>
                  <p>{show.actors}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showRatingOnlyModal && selectedShowForRating && (
        <RatingOnlyModal
          show={selectedShowForRating}
          onClose={() => {
            setShowRatingOnlyModal(false);
            setSelectedShowForRating(null);
          }}
          onSubmit={handleRatingSubmit}
        />
      )}

      {showRatingsOverview && (
        <RatingsOverviewModal
          show={show}
          ratings={showRatings}
          isLoading={ratingsLoading}
          onClose={() => setShowRatingsOverview(false)}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {showStreamingModal && selectedShowForSources && (
        <StreamingSourceModal
          show={selectedShowForSources}
          existingSources={selectedShowForSources.streamingSources || []}
          onClose={() => {
            setShowStreamingModal(false);
            setSelectedShowForSources(null);
          }}
          onAddSource={handleAddStreamingSource}
          onRemoveSource={handleRemoveStreamingSource}
        />
      )}
    </div>
  );
}

export default ShowDetails; 